import {observer} from 'mobx-react-lite'
import React, {useEffect, useMemo} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {PermissionRoleKey} from 'yougile-model'
import {DropdownList} from '@components/common/base/dropdown-list'
import {showConfirm} from '@components/common/base/notifications'
import {
  getDefaultRoles,
  useCompanyInvitePopupStore
} from '@components/dashboard/company-invite-popup/company-invite-popup-store'
import {EmailsInputWithErrors} from '@components/dashboard/company-invite-popup/emails-input-with-errors'
import {useForceUpdate, useGlobalKeyDown} from '@containers/hooks'
import {useProjectParticipantStore} from './project-participants-store'

const TwCaption = tw.div`
  h2-semibold
  text-primary
  cursor-default
`

export const InviteTabContent = observer(() => {
  const [, store] = useProjectParticipantStore()
  const [, inviteStore] = useCompanyInvitePopupStore()

  useEffect(() => {
    inviteStore.clearEmails()
  }, [])

  useGlobalKeyDown({
    escape: () => {
      if (inviteStore.emails.length) {
        void showConfirm(Lang.msg.proj.unsavedChangesHint).then(data => {
          data && store.close()
        })
      } else {
        store.close()
      }

      return true
    },
    enter: () => {
      if (inviteStore.canSubmit()) {
        inviteStore.onSubmit()
        if (!store.props.project?.id) {
          store.updateInvited(inviteStore.emails.map(e => e.text))
        }
        store.close()
      }
      return true
    }
  })

  const forceUpdate = useForceUpdate()

  const onRolesChanged = (value: PermissionRoleKey) => {
    const projectId = store.props.project?.id
    if (projectId) {
      inviteStore.onDefaultRoleChanged(projectId, value)
    } else {
      inviteStore.setDefaultRoleForNewProject(value)
      store.setDefaultRoleForNewProject(value)
    }
    forceUpdate()
  }

  const defaultRole =
    inviteStore.projects?.[0]?.defaultRole ||
    inviteStore.state.roleForNewProject ||
    'worker'

  const options = useMemo(() => {
    const roles = inviteStore.projects?.[0]?.roles || getDefaultRoles()
    return roles?.map(r => ({
      value: r.role,
      label: r.text
    }))
  }, [inviteStore.projects?.[0]])

  return (
    <>
      <div className="pt-8 flex flex-col gap-8">
        <TwCaption>{Lang.msg.invitePopup.inviteCaption}</TwCaption>

        <EmailsInputWithErrors
          size="small"
          className="mt-[-2px] ml-[-1px]"
          esc={() => {
            if (inviteStore.emails.length) {
              void showConfirm(Lang.msg.proj.unsavedChangesHint).then(data => {
                data && store.close()
              })
            } else {
              store.close()
            }
          }}
          enter={() => {
            if (inviteStore.canSubmit()) {
              inviteStore.onSubmit()
              if (!store.props.project?.id) {
                store.updateInvited(inviteStore.emails.map(e => e.text))
              }
              store.close()
            }
          }}
        />

        {!!options.length && (
          <div className="flex items-center gap-8 justify-end h-16 pt-8 pb-24">
            <span className="text-sm-regular text-primary cursor-default">
              {Lang.msg.invitePopup.roleForInvitees}
            </span>

            <DropdownList
              link
              value={defaultRole}
              options={options}
              size={'small'}
              selectStyle={{width: 'fit-content'}}
              onChange={onRolesChanged}
            />
          </div>
        )}
      </div>
    </>
  )
})
